/**
 * File Load Handler Module
 * Handles file selection modal and local data loading
 */

import { DataLoader } from '../data/data-loader';
import { TreeMetadataSync } from '../sync/tree-metadata-sync';
import { fetchNewickFromUrl } from './url-fetch';
import { validateNewick } from './newick-validate';

export class UploadHandler {
  private viewer: any;
  private files: Array<{ id: number; file: File; type: string }>;
  private fileIdCounter: number;

  constructor(viewer: any) {
    this.viewer = viewer;
    this.files = [];
    this.fileIdCounter = 0;
    this.initModal();
  }

  initModal(): void {
    const uploadBtn = document.getElementById('upload-data-btn')!;
    const modal = document.getElementById('upload-modal')!;
    const closeBtn = document.getElementById('modal-close-btn')!;
    const loadDataBtn = document.getElementById('load-data-btn')!;
    const resetDemoBtn = document.getElementById('reset-demo-btn')!;
    const dropzone = document.getElementById('universal-dropzone')!;
    const fileInput = document.getElementById('universal-file-input') as HTMLInputElement;

    uploadBtn.addEventListener('click', () => { modal.classList.remove('hidden'); });

    const closeModal = () => { modal.classList.add('hidden'); };

    closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.classList.contains('hidden')) closeModal();
    });

    this.setupUniversalDropzone(dropzone, fileInput);

    loadDataBtn.addEventListener('click', async () => {
      await this.loadUserData();
      closeModal();
    });

    const fetchUrlBtn = document.getElementById('fetch-url-btn');
    const urlInput = document.getElementById('tree-url-input') as HTMLInputElement | null;

    const fetchFromUrl = async () => {
      if (await this.loadFromUrl()) closeModal();
    };

    fetchUrlBtn?.addEventListener('click', fetchFromUrl);
    urlInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void fetchFromUrl();
      }
    });

    resetDemoBtn.addEventListener('click', async () => {
      await this.resetToDemoData();
      closeModal();
    });
  }

  setupUniversalDropzone(dropzone: HTMLElement, fileInput: HTMLInputElement): void {
    const browseBtn = document.getElementById('browse-files-btn');
    if (browseBtn) {
      browseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        fileInput.click();
      });
    }

    dropzone.addEventListener('click', (e) => {
      if (!(e.target as Element).closest('.file-list-item') && !(e.target as Element).closest('#browse-files-btn')) {
        fileInput.click();
      }
    });

    fileInput.addEventListener('change', (e) => {
      const files = Array.from((e.target as HTMLInputElement).files ?? []);
      files.forEach(file => this.addFile(file));
      fileInput.value = '';
    });

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('dragover');
    });

    dropzone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      dropzone.classList.remove('dragover');
    });

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('dragover');
      const files = Array.from(e.dataTransfer?.files ?? []);
      files.forEach(file => this.addFile(file));
    });
  }

  detectFileType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    if (['nwk', 'newick', 'tree'].includes(ext)) return 'tree';
    if (['csv', 'tsv', 'txt'].includes(ext)) return 'metadata';
    return 'unknown';
  }

  addFile(file: File): void {
    const fileId = this.fileIdCounter++;
    const detectedType = this.detectFileType(file.name);
    this.files.push({ id: fileId, file, type: detectedType });
    this.renderFileList();
    this.updateLoadButton();
  }

  removeFile(fileId: number): void {
    this.files = this.files.filter(f => f.id !== fileId);
    this.renderFileList();
    this.updateLoadButton();
  }

  changeFileType(fileId: number, newType: string): void {
    const fileObj = this.files.find(f => f.id === fileId);
    if (fileObj) {
      fileObj.type = newType;
      this.renderFileList();
      this.updateLoadButton();
    }
  }

  renderFileList(): void {
    const fileListContainer = document.getElementById('file-list-container')!;
    const dropzoneContent = document.getElementById('dropzone-content')!;

    if (this.files.length === 0) {
      fileListContainer.innerHTML = '';
      fileListContainer.classList.add('hidden');
      dropzoneContent.classList.remove('hidden');
      return;
    }

    dropzoneContent.classList.add('hidden');
    fileListContainer.classList.remove('hidden');
    fileListContainer.innerHTML = '<div class="file-list">' +
      this.files.map(fileObj => this.renderFileItem(fileObj)).join('') +
      '</div>';

    this.attachFileListHandlers();
  }

  renderFileItem(fileObj: { id: number; file: File; type: string }): string {
    const fileSize = this.formatFileSize(fileObj.file.size);
    const fileIcon = this.getFileIcon(fileObj.file.name);
    const treeSelected = fileObj.type === 'tree' ? 'selected' : '';
    const metaSelected = fileObj.type === 'metadata' ? 'selected' : '';
    const unknownSelected = fileObj.type === 'unknown' ? 'selected' : '';

    return '<div class="file-list-item" data-file-id="' + fileObj.id + '">' +
      '<span class="file-item-icon">' + fileIcon + '</span>' +
      '<div class="file-item-details">' +
        '<div class="file-item-name">' + fileObj.file.name + '</div>' +
        '<div class="file-item-size">' + fileSize + '</div>' +
      '</div>' +
      '<select class="file-type-selector" data-file-id="' + fileObj.id + '">' +
        '<option value="tree" ' + treeSelected + '>Tree</option>' +
        '<option value="metadata" ' + metaSelected + '>Metadata</option>' +
        '<option value="unknown" ' + unknownSelected + '>Unknown</option>' +
      '</select>' +
      '<button class="file-item-remove" data-file-id="' + fileObj.id + '" title="Remove file">×</button>' +
    '</div>';
  }

  attachFileListHandlers(): void {
    const selectors = document.querySelectorAll<HTMLSelectElement>('.file-type-selector');
    selectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const fileId = parseInt((e.target as HTMLSelectElement).dataset['fileId'] ?? '');
        const newType = (e.target as HTMLSelectElement).value;
        this.changeFileType(fileId, newType);
      });
    });

    const removeButtons = document.querySelectorAll<HTMLButtonElement>('.file-item-remove');
    removeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const fileId = parseInt((e.target as HTMLButtonElement).dataset['fileId'] ?? '');
        this.removeFile(fileId);
      });
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + (sizes[i] ?? 'Bytes');
  }

  getFileIcon(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    if (['nwk', 'newick', 'tree'].includes(ext)) return '\u{1f333}';
    if (['csv', 'tsv', 'txt'].includes(ext)) return '\u{1f4ca}';
    return '\u{1f4c4}';
  }

  updateLoadButton(): void {
    const loadDataBtn = document.getElementById('load-data-btn') as HTMLButtonElement;
    const hasTreeFile = this.files.some(f => f.type === 'tree');
    loadDataBtn.disabled = !hasTreeFile;
  }

  async loadUserData(): Promise<void> {
    const treeFiles = this.files.filter(f => f.type === 'tree');
    const metadataFiles = this.files.filter(f => f.type === 'metadata');

    if (treeFiles.length === 0) {
      alert('Please select at least one tree file');
      return;
    }

    if (treeFiles.length > 1) {
      alert('Multiple tree files detected. Using the first one: ' + treeFiles[0]!.file.name);
    }

    try {
      document.getElementById('loading')!.classList.remove('hidden');

      const treeText = await this.readFileAsText(treeFiles[0]!.file);

      // Checked before anything is handed to the viewer: the vendored parser
      // never throws, so an unchecked bad file renders an empty canvas with no
      // indication of what went wrong.
      const validation = validateNewick(treeText);
      if (!validation.valid) {
        throw new Error(`"${treeFiles[0]!.file.name}" is not a valid Newick tree. ${validation.error}`);
      }
      console.log('[UploadHandler] Newick validated:', validation.tipCount, 'tips');

      let metadataRows: Record<string, string>[] = [];
      if (metadataFiles.length > 0) {
        const metadataText = await this.readFileAsText(metadataFiles[0]!.file);
        metadataRows = this.parseMetadataFile(metadataText, metadataFiles[0]!.file.name);
        if (metadataFiles.length > 1) {
          console.log('[UploadHandler] Multiple metadata files detected. Using the first one:', metadataFiles[0]!.file.name);
        }
      } else {
        metadataRows = this.createMinimalMetadata(treeText);
      }

      await this.reloadViewer(treeText, metadataRows);
      this.clearFileSelections();
      document.getElementById('loading')!.classList.add('hidden');
      console.log('[UploadHandler] User data loaded successfully');
    } catch (error: any) {
      console.error('[UploadHandler] Failed to load user data:', error);
      document.getElementById('loading')!.classList.add('hidden');
      alert('Failed to load data: ' + error.message);
    }
  }

  /**
   * Load a tree from an HTTP/HTTPS URL, mirroring {@link loadUserData} but
   * sourcing the Newick text from the network instead of a local file.
   * Tree-only and session-only, exactly like a single-file upload.
   * Returns `true` on success so the caller can close the modal; `false`
   * leaves it open so the user can correct the URL.
   */
  async loadFromUrl(): Promise<boolean> {
    const urlInput = document.getElementById('tree-url-input') as HTMLInputElement | null;
    const rawUrl = urlInput?.value ?? '';

    if (!rawUrl.trim()) {
      alert('Please enter a URL.');
      return false;
    }

    try {
      document.getElementById('loading')!.classList.remove('hidden');

      const treeText = await fetchNewickFromUrl(rawUrl);
      const metadataRows = this.createMinimalMetadata(treeText);

      await this.reloadViewer(treeText, metadataRows);
      this.clearFileSelections();
      if (urlInput) urlInput.value = '';

      document.getElementById('loading')!.classList.add('hidden');
      console.log('[UploadHandler] Tree loaded from URL successfully');
      return true;
    } catch (error: any) {
      console.error('[UploadHandler] Failed to load tree from URL:', error);
      document.getElementById('loading')!.classList.add('hidden');
      alert('Failed to fetch tree: ' + error.message);
      return false;
    }
  }

  async resetToDemoData(): Promise<void> {
    try {
      document.getElementById('loading')!.classList.remove('hidden');
      window.location.reload();
    } catch (error: any) {
      console.error('[UploadHandler] Failed to reset to demo data:', error);
      document.getElementById('loading')!.classList.add('hidden');
      alert('Failed to reset to demo data: ' + error.message);
    }
  }

  readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => { resolve((e.target as FileReader).result as string); };
      reader.onerror = () => { reject(new Error('Failed to read file')); };
      reader.readAsText(file);
    });
  }

  parseMetadataFile(text: string, filename: string): Record<string, string>[] {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    const delimiter = ext === 'tsv' ? '\t' : ',';

    const lines = text.trim().split(/\r?\n|\r/);
    console.log('[UploadHandler] parseMetadataFile:', {
      filename,
      totalLines: lines.length,
      firstLine: lines[0]?.substring(0, 100),
    });

    if (lines.length === 0) return [];

    const headers = lines[0]!.split(delimiter).map(h => h.trim());
    console.log('[UploadHandler] Headers detected:', headers.length, headers.slice(0, 5));

    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i]!.trim()) continue;
      const values = lines[i]!.split(delimiter).map(v => v.trim());
      const row: Record<string, string> = {};
      headers.forEach((header, index) => { row[header] = values[index] ?? ''; });
      rows.push(row);
    }

    console.log('[UploadHandler] Parsed rows:', rows.length, 'First row keys:', Object.keys(rows[0] ?? {}));
    return rows;
  }

  /**
   * Build a one-column metadata table from the tree itself, for a tree loaded
   * without a metadata file.
   *
   * Tip labels come from the Newick parser rather than a regular expression: a
   * pattern anchored on ":" finds nothing in a tree with no branch lengths, and
   * truncates identifiers containing "-" or "." to their last segment, which
   * then fails to match the labels the renderer draws.
   */
  createMinimalMetadata(treeText: string): Record<string, string>[] {
    return validateNewick(treeText).tipLabels.map(label => ({ id: label }));
  }

  async reloadViewer(treeText: string, metadataRows: Record<string, string>[]): Promise<void> {
    console.log('[UploadHandler] reloadViewer called with:', {
      treeLength: treeText?.length,
      metadataRowCount: metadataRows?.length,
      firstRow: metadataRows?.[0],
    });

    this.viewer.metadata = metadataRows;
    this.viewer.tree = treeText;
    this.viewer.selectedRows.clear();

    console.log('[UploadHandler] Viewer metadata set:', {
      length: this.viewer.metadata.length,
      firstRow: this.viewer.metadata[0],
    });

    this.viewer.columnTypes = DataLoader.detectColumnTypes(metadataRows);

    const treeData = this.viewer.parseTree(treeText);

    let labelField: string;
    if (metadataRows.length > 0 && Object.keys(metadataRows[0]!).length > 0) {
      labelField = this.viewer.autoDetectLabelField(treeData, metadataRows);
    } else {
      labelField = 'id';
    }

    console.log('[UploadHandler] Label field detected:', labelField);

    TreeMetadataSync.buildLabelMapping(treeData, metadataRows, labelField);

    this.viewer.initControls();

    console.log('[UploadHandler] Calling renderTable...');
    this.viewer.renderTable();
    console.log('[UploadHandler] Calling renderTree...');
    this.viewer.renderTree();

    console.log('[UploadHandler] Viewer reloaded with new data');
  }

  clearFileSelections(): void {
    this.files = [];
    this.fileIdCounter = 0;
    const fileInput = document.getElementById('universal-file-input') as HTMLInputElement | null;
    if (fileInput) fileInput.value = '';
    this.renderFileList();
    this.updateLoadButton();
  }
}
